import { AgendaItemTypeEnum } from "enums/AgendaItemTypeEnum";
import { camelToDash } from "helpers";
import { AgendaItemModel } from "models/AgendaItemModel";
import { NumberChoiceModel } from "models/ChoiceModel";
import { useBoard } from "providers";
import { useTranslations } from "providers";
import React, { PropsWithChildren, useCallback, useMemo } from "react";

import { AgendaItemTypesContext, AgendaItemTypesContextProps } from "./AgendaItemTypesContext";

export type AgendaItemTypeRaw = AgendaItemTypeEnum | number | string | undefined;

export const AgendaItemTypesProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const { isOwner } = useBoard();

  const canEditPause = useCallback(() => {
    return isOwner;
  }, [isOwner]);

  const canEditCategory = useCallback(() => {
    return isOwner;
  }, [isOwner]);

  const getEnum = useCallback((value: AgendaItemTypeRaw): AgendaItemTypeEnum => {
    const enumValue = AgendaItemTypeEnum[value as keyof typeof AgendaItemTypeEnum];
    if (!enumValue) throw new Error(`Unhandled enum value '${value}'!`);
    return enumValue;
  }, []);

  const getClassName = useCallback(
    (item: AgendaItemModel) => {
      const type = item.type;
      const enumValue = getEnum(type);
      const className = camelToDash(enumValue.toString());
      if (!className) throw new Error(`Unhandled className '${type}': ${JSON.stringify(item)}!`);
      return className;
    },
    [getEnum]
  );

  const isDisabled = useCallback(
    (type: AgendaItemTypeRaw) => {
      const enumValue = getEnum(type);
      if (enumValue === AgendaItemTypeEnum.Category && !canEditCategory()) return true;
      if (enumValue === AgendaItemTypeEnum.Break && !canEditPause()) return true;
      return false;
    },
    [getEnum, canEditCategory, canEditPause]
  );

  const agendaItemTypes = useMemo<NumberChoiceModel[]>(
    () => [
      {
        value: AgendaItemTypeEnum.Empty,
        name: AgendaItemTypeEnum[AgendaItemTypeEnum.Empty],
        text: i18n.AgendaItemType_Placeholder,
        tooltip: "",
        disabled: false,
        isDefault: false,
      },
      {
        value: AgendaItemTypeEnum.Decision,
        name: AgendaItemTypeEnum[AgendaItemTypeEnum.Decision],
        text: i18n.AgendaItemType_Decision,
        tooltip: i18n.AgendaItemType_Decision_Tooltip,
        disabled: false,
        isDefault: false,
      },
      {
        value: AgendaItemTypeEnum.Decision_eDecide,
        name: AgendaItemTypeEnum[AgendaItemTypeEnum.Decision_eDecide],
        text: i18n.AgendaItemType_Decision_eDecide,
        tooltip: "",
        disabled: false,
        isDefault: false,
      },
      {
        value: AgendaItemTypeEnum.Information,
        name: AgendaItemTypeEnum[AgendaItemTypeEnum.Information],
        text: i18n.AgendaItemType_Information,
        tooltip: i18n.AgendaItemType_Information_Tooltip,
        disabled: false,
        isDefault: false,
      },
      {
        value: AgendaItemTypeEnum.Information_eDecide,
        name: AgendaItemTypeEnum[AgendaItemTypeEnum.Information_eDecide],
        text: i18n.AgendaItemType_Information_eDecide,
        tooltip: "",
        disabled: false,
        isDefault: false,
      },
      {
        value: AgendaItemTypeEnum.Other,
        name: AgendaItemTypeEnum[AgendaItemTypeEnum.Other],
        text: i18n.AgendaItemType_Other,
        tooltip: "",
        disabled: false,
        isDefault: false,
      },
      {
        value: AgendaItemTypeEnum.Break,
        name: AgendaItemTypeEnum[AgendaItemTypeEnum.Break],
        text: i18n.AgendaItemType_Break,
        tooltip: "",
        disabled: isDisabled(AgendaItemTypeEnum.Break),
        isDefault: false,
      },
      {
        value: AgendaItemTypeEnum.Category,
        name: AgendaItemTypeEnum[AgendaItemTypeEnum.Category],
        text: i18n.AgendaItemType_Category,
        tooltip: "",
        disabled: isDisabled(AgendaItemTypeEnum.Category),
        isDefault: false,
      },
      {
        value: AgendaItemTypeEnum.Discussion,
        name: AgendaItemTypeEnum[AgendaItemTypeEnum.Discussion],
        text: i18n.AgendaItemType_Discussion,
        tooltip: "",
        disabled: isDisabled(AgendaItemTypeEnum.Discussion),
        isDefault: false,
      },
    ],
    [i18n, isDisabled]
  );

  const value = useMemo<AgendaItemTypesContextProps>(
    () => ({
      agendaItemTypes,
      canEditPause,
      canEditCategory,
      getClassName,
    }),
    [agendaItemTypes, canEditPause, canEditCategory, getClassName]
  );

  return <AgendaItemTypesContext.Provider value={value}>{children}</AgendaItemTypesContext.Provider>;
};
